import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  IconButton,
  TextField,
  MenuItem,
  Chip,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import { Edit, Delete, Groups, People } from "@mui/icons-material";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FaqButton } from "../components/FaqButton";
import { FooterInternal } from "../components/Footer";
import AddButton from "../components/AddButton";
import ClearFiltersButton from "../components/ClearFiltersButton";
import { useRouter } from "../contexts/RouterContext";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../hooks/useToast";
import { Toast } from "../components/Toast";
import ConfirmModal from "../components/modals/ConfirmModal";
import TeamModal from "../components/modals/TeamModal";
import TeamMembersModal from "../components/modals/TeamMembersModal";
import TeamPatientsModal from "../components/modals/TeamPatientsModal";
import {
  professionalTeamService,
  ProfessionalTeamItem,
} from "../services/professionalTeamService";
import { colors, typography, pageHeader } from "../theme/designSystem";
import { filterFieldSx } from "../theme/filterStyles";
import ResultCount from "../components/ResultCount";
import StandardPagination, { ITENS_POR_PAGINA_PADRAO } from "../components/Pagination/StandardPagination";

// Gestão de equipes da entidade da sessão (acessada pela ação na lista de entidades).
// Escrita dupla no backend: Bubble + base nova.
const EntityTeams: React.FC = () => {
  const { navigateTo, getAllParams } = useRouter();
  const { user } = useAuth();
  const { toast, showToast, hideToast } = useToast();

  const params = getAllParams();
  const entityName = params.get("entityName") || user?.entityName || "Entidade";

  const [teams, setTeams] = useState<ProfessionalTeamItem[]>([]);
  const [statusFilter, setStatusFilter] = useState<"active" | "inactive" | "all">(
    "active"
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(ITENS_POR_PAGINA_PADRAO);
  const [loading, setLoading] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState<ProfessionalTeamItem | null>(null);
  const [membersOf, setMembersOf] = useState<ProfessionalTeamItem | null>(null);
  const [patientsOf, setPatientsOf] = useState<ProfessionalTeamItem | null>(null);
  const [toDelete, setToDelete] = useState<ProfessionalTeamItem | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await professionalTeamService.getAll();
      setTeams(data);
    } catch (error) {
      console.error("Erro ao carregar equipes:", error);
      showToast("Erro ao carregar as equipes.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = `${entityName} / Equipes`;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = teams.filter((t) =>
    statusFilter === "all" ? true : statusFilter === "active" ? t.isActive : !t.isActive
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / itemsPerPage));
  const paged = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  // Mexeu no filtro, volta para a primeira pagina: senao a lista aparece vazia sem motivo
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter]);


  const openCreate = () => {
    setEditing(null);
    setIsModalOpen(true);
  };

  const openEdit = (team: ProfessionalTeamItem) => {
    setEditing(team);
    setIsModalOpen(true);
  };

  const handleToggleStatus = async (team: ProfessionalTeamItem) => {
    try {
      await professionalTeamService.setStatus(team.id, !team.isActive);
      showToast(
        team.isActive ? "Equipe inativada." : "Equipe reativada.",
        "success"
      );
      await load();
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { description?: string } } })?.response
          ?.data?.description || "Erro ao alterar o status da equipe.";
      showToast(message, "error");
    }
  };

  const handleDelete = async () => {
    if (!toDelete || deleting) return;
    setDeleting(true);
    try {
      await professionalTeamService.remove(toDelete.id);
      showToast("Equipe inativada com sucesso.", "success");
      setToDelete(null);
      await load();
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { description?: string } } })?.response
          ?.data?.description || "Erro ao excluir a equipe.";
      showToast(message, "error");
    } finally {
      setDeleting(false);
    }
  };

  const handleSaved = async () => {
    showToast("Equipe salva com sucesso.", "success");
    await load();
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <HeaderInternal
        showCTAButton={false}
        className="login-header"
        isLoggedIn={true}
        userEmail={user?.email || ""}
        userProfile={user?.role || ""}
        clinicName={user?.clinicName || "Clínica"}
        notificationCount={0}
        onRevalidateLogin={() => {}}
        onNotificationClick={() => {}}
        onUserClick={() => {}}
        onLogoClick={() => navigateTo("dashboard")}
      />

      <Box
        component="main"
        className="c4u-main"
      >
        <Container maxWidth={false} disableGutters sx={pageHeader.container}>
          <div className="c4u-page-header c4u-page-header--in-panel">
            <div className="c4u-page-header__texts">
              <h1 className="c4u-page-header__title">{entityName} / Equipes</h1>
              <p className="c4u-page-header__subtitle">Equipes de profissionais da entidade e sua composição.</p>
            </div>
            <div className="c4u-page-header__actions">
              <FaqButton />
            </div>
          </div>

          <div className="c4u-panel">
          <div className="c4u-filters__row">
            <div className="c4u-filters__fields">
              <TextField
                select
                size="small"
                label="Status"
                value={statusFilter}
                onChange={(e) =>
                  setStatusFilter(e.target.value as "active" | "inactive" | "all")
                }
                sx={filterFieldSx}
                InputLabelProps={{ shrink: true }}
              >
                <MenuItem value="active">Ativas</MenuItem>
                <MenuItem value="inactive">Inativas</MenuItem>
                <MenuItem value="all">Todas</MenuItem>
              </TextField>
            </div>
            <div className="c4u-filters__actions">
              <ClearFiltersButton
                onClick={() => setStatusFilter("active")}
                disabled={statusFilter === "active"}
              />
              <AddButton onClick={openCreate} title="Adicionar equipe" />
            </div>
          </div>

          <ResultCount shown={filtered.length} total={teams.length} />

          <Box className="admin-plans-list-container" sx={{ background: "none", border: "none", borderRadius: 0, boxShadow: "none" }}>
            <Box className="admin-plans-table admin-entities-table">
              <Box className="c4u-table__header">
                <Box
                  className="admin-plans-header-cell"
                  sx={{ flex: "0 0 220px", textAlign: "left" }}
                >
                  Equipe
                </Box>
                <Box
                  className="admin-plans-header-cell c4u-col--xl"
                  sx={{ flex: "1 1 auto", minWidth: 160, textAlign: "left" }}
                >
                  Descrição
                </Box>
                <Box
                  className="admin-plans-header-cell c4u-col--xl"
                  sx={{ flex: "0 0 90px", textAlign: "left" }}
                >
                  Principal
                </Box>
                <Box
                  className="admin-plans-header-cell c4u-col--md"
                  sx={{ flex: "0 0 240px", textAlign: "left", pr: 1.5 }}
                >
                  Coordenador
                </Box>
                <Box
                  className="admin-plans-header-cell c4u-col--lg"
                  sx={{ flex: "0 0 110px", textAlign: "left" }}
                >
                  Profissionais
                </Box>
                <Box
                  className="admin-plans-header-cell c4u-col--lg"
                  sx={{ flex: "0 0 90px", textAlign: "left" }}
                >
                  Pacientes
                </Box>
                <Box
                  className="admin-plans-header-cell"
                  sx={{ flex: "0 0 90px", textAlign: "left" }}
                >
                  Status
                </Box>
                <Box
                  className="admin-plans-header-cell"
                  sx={{ flex: "0 0 165px", justifyContent: "flex-end" }}
                >
                  Ações
                </Box>
              </Box>

              <Box className="admin-plans-table-body">
                {loading ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      py: 6,
                    }}
                  >
                    <CircularProgress size={28} sx={{ color: colors.primary }} />
                  </Box>
                ) : filtered.length === 0 ? (
                  <Box sx={{ py: 6, textAlign: "center" }}>
                    <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                      Nenhuma equipe para este filtro.
                    </Typography>
                  </Box>
                ) : (
                  paged.map((team) => (
                    <Box
                      key={team.id}
                      className="c4u-table__row"
                    >
                      <Box
                        className="admin-plans-cell"
                        sx={{
                          flex: "0 0 220px",
                          textAlign: "left",
                          fontWeight: 600,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                        data-label="Equipe"
                        title={team.title}
                      >
                        {team.title}
                      </Box>
                      <Box
                        className="admin-plans-cell c4u-col--xl"
                        sx={{
                          flex: "1 1 auto",
                          minWidth: 160,
                          textAlign: "left",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                        data-label="Descrição"
                        title={team.description || undefined}
                      >
                        {team.description || "-"}
                      </Box>
                      <Box
                        className="admin-plans-cell c4u-col--xl"
                        sx={{ flex: "0 0 90px", textAlign: "left" }}
                        data-label="Principal"
                      >
                        {team.mainTeam ? "sim" : "-"}
                      </Box>
                      <Box
                        className="admin-plans-cell c4u-col--md"
                        sx={{
                          flex: "0 0 240px",
                          textAlign: "left",
                          // Nome completo sempre visível: quebra de linha em vez de reticências
                          whiteSpace: "normal",
                          overflowWrap: "break-word",
                          pr: 1.5,
                        }}
                        data-label="Coordenador"
                      >
                        {team.coordinatorName || "-"}
                      </Box>
                      <Box
                        className="admin-plans-cell c4u-col--lg"
                        sx={{ flex: "0 0 110px", textAlign: "left" }}
                        data-label="Profissionais"
                      >
                        {team.memberCount}
                      </Box>
                      <Box
                        className="admin-plans-cell c4u-col--lg"
                        sx={{ flex: "0 0 90px", textAlign: "left" }}
                        data-label="Pacientes"
                        title={
                          team.specificPatients
                            ? "Pacientes específicos da equipe"
                            : "A equipe atende todos os pacientes da entidade"
                        }
                      >
                        {team.specificPatients ? team.patientCount : "todos"}
                      </Box>
                      <Box
                        className="admin-plans-cell"
                        sx={{ flex: "0 0 90px", textAlign: "left" }}
                        data-label="Status"
                      >
                        <Chip
                          label={team.isActive ? "Ativa" : "Inativa"}
                          size="small"
                          onClick={() => handleToggleStatus(team)}
                          title={
                            team.isActive
                              ? "Clique para inativar"
                              : "Clique para reativar"
                          }
                          sx={{
                            color: "white",
                            cursor: "pointer",
                            backgroundColor: team.isActive
                              ? colors.success || "#28a745"
                              : "#adb5bd",
                          }}
                        />
                      </Box>
                      <Box
                        className="admin-plans-cell admin-plans-actions"
                        sx={{ flex: "0 0 165px", textAlign: "right" }}
                        data-label="Ações"
                      >
                        <Box
                          sx={{
                            display: "flex",
                            gap: 0.5,
                            justifyContent: "flex-end",
                          }}
                        >
                          <Tooltip title="Profissionais da equipe">
                            <IconButton
                              className="c4u-row-action"
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setMembersOf(team);
                              }}
                              sx={{
                                backgroundColor: "#7b1fa2",
                                color: "white",
                                "&:hover": { backgroundColor: "#6a1b9a" },
                              }}
                            >
                              <Groups sx={{ fontSize: "1rem" }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Pacientes da equipe">
                            <IconButton
                              className="c4u-row-action"
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setPatientsOf(team);
                              }}
                              sx={{
                                backgroundColor: "#28a745",
                                color: "white",
                                "&:hover": { backgroundColor: "#218838" },
                              }}
                            >
                              <People sx={{ fontSize: "1rem" }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Editar">
                            <IconButton
                              className="c4u-row-action"
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                openEdit(team);
                              }}
                              sx={{
                                backgroundColor: "#17a2b8",
                                color: "white",
                                "&:hover": { backgroundColor: "#138496" },
                              }}
                            >
                              <Edit sx={{ fontSize: "1rem" }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Excluir (inativa a equipe)">
                            <IconButton
                              className="c4u-row-action"
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setToDelete(team);
                              }}
                              sx={{
                                backgroundColor: "#dc3545",
                                color: "white",
                                "&:hover": { backgroundColor: "#c82333" },
                              }}
                            >
                              <Delete sx={{ fontSize: "1rem" }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    </Box>
                  ))
                )}
              </Box>
            </Box>

            {filtered.length > 0 && (
              <StandardPagination
                currentPage={currentPage}
                totalPages={totalPages}
                itemsPerPage={itemsPerPage}
                totalItems={filtered.length}
                onPageChange={setCurrentPage}
                onItemsPerPageChange={(n) => {
                  setItemsPerPage(n);
                  setCurrentPage(1);
                }}
              />
            )}
          </Box>
          </div>
        </Container>
      </Box>

      <FooterInternal className="login-footer-component" />

      <TeamModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        value={editing}
        onSaved={handleSaved}
      />

      <TeamMembersModal
        isOpen={!!membersOf}
        onClose={() => {
          setMembersOf(null);
          load();
        }}
        teamId={membersOf?.id || ""}
        teamTitle={membersOf?.title || ""}
      />

      <TeamPatientsModal
        isOpen={!!patientsOf}
        onClose={() => {
          setPatientsOf(null);
          load();
        }}
        teamId={patientsOf?.id || ""}
        teamTitle={patientsOf?.title || ""}
      />

      <ConfirmModal
        isOpen={!!toDelete}
        title="Excluir Equipe"
        message={`Deseja realmente excluir a equipe "${toDelete?.title || ""}"? Ela será inativada e pode ser reativada pelo filtro de status.`}
        confirmButtonText="Excluir"
        onConfirm={handleDelete}
        onCancel={() => setToDelete(null)}
        type="danger"
        isProcessing={deleting}
      />

      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={hideToast}
      />
    </Box>
  );
};

export default EntityTeams;
